"use client";

import React, {useMemo} from "react";
import { Stack, Typography } from "@mui/material";
import {useLocale, useTranslations} from "next-intl";
import { ColletteDivider, ColletteIconButton } from "@collette-travel/collette-common-controls";
import { Close } from "@mui/icons-material";
import HubspotFormDisclaimer from "@/components/forms/disclaimer/hubspot-form-disclaimer";
import { useForm } from "react-hook-form";
import DataLayer from "@/components/data-layer/data-layer";
import racDataLayerPushFactory from "@/factories/data-layer/rac-data-layer-push-factory";
import useLeadSubmit from "@/hooks/forms/on-submit/use-lead-submit";
import {RACFormData} from "@/types/leads/lead-forms";
import RequestACallbackSubmissionMessage
  from "@/components/forms/request-a-callback/request-a-callback-submission-message";
import RequestACallbackForm from "@/components/forms/request-a-callback/request-a-callback-form";

interface RequestACallbackProps {
  handleClose: () => void;
}

const RequestACallbackSection = ({
  handleClose
}: RequestACallbackProps) => {
  const t = useTranslations("FORMS.RAC");
  const sourceUrl = window.location.href;
  const locale = useLocale();

  const {
    handleSubmit,
    formState: { isSubmitSuccessful, isSubmitted, isSubmitting, isValid },
    control,
    getValues
  } = useForm<RACFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      isTravelAgent: false,
      workingWithAgent: false,
      withMoreTravelers: false,
      recaptchaToken: undefined
    }
  });

  const { onSubmit, serverError } = useLeadSubmit<RACFormData>({
    locale,
    sourceUrl,
    leadType: "RAC"
  });
  
  const dataLayerData = useMemo(() => 
    racDataLayerPushFactory(getValues(), sourceUrl),
    [getValues, sourceUrl, isSubmitSuccessful]); // needs to update when isSubmitted changes

  return (
    <Stack
      sx={{
        borderRadius: "1rem",
        backgroundColor: "background.default"
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        gap="1rem"
        alignItems="flex-start"
        padding={{ xs: "2rem 2.4rem", md: "4rem 4rem 0" }}
      >
        <Stack flexGrow={1}>
          <Typography variant="h3" mb="1.2rem" data-testid="rac-header">
            {t("Request a Callback")}
          </Typography>
          {!isSubmitSuccessful && (
            <Typography mb="2.4rem" data-testid="rac-copy">
              {t("copy")}
            </Typography>
          )}
          <ColletteDivider sx={{ borderColor: "divider" }} />
        </Stack>
        <ColletteIconButton
          testId="dialog-close-button"
          onClick={handleClose}
          sx={{
            mt: "-0.8rem",
            mr: "-0.8rem"
          }}
        >
          <Close />
        </ColletteIconButton>
      </Stack>
      {/* Form and submission response */}
      {isSubmitSuccessful && (
        <RequestACallbackSubmissionMessage handleClose={handleClose} error={serverError} />
      )}
      {!isSubmitSuccessful && (
        <RequestACallbackForm
          isInvalid={!isValid && isSubmitted}
          control={control}
          handleSubmit={handleSubmit(onSubmit)}
          isSubmitting={isSubmitting}
        />
      )}
      {/* Privacy Policy */}
      {!isSubmitSuccessful && <HubspotFormDisclaimer testId="rac-disclaimer" />}
      {isSubmitSuccessful && <DataLayer data={dataLayerData} />}
    </Stack>
  );
};

export default RequestACallbackSection;
